import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileUp, RotateCw } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadError } from '../../components/layout/LoadError';
import { useToast } from '../../components/ui/toast';
import {
  useCreatePolicy,
  useCreatePolicyVersion,
  useEmployees,
  useOrgDepartments,
  usePolicies,
  type CreatePolicyInput,
  type PolicyAckMode,
  type PolicyAudience,
  type PolicyKind,
} from '../../hooks';
import { ApiError, putPresignedFile } from '../../lib/api-client';

interface EmployeeOption {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  department?: string | null;
  status?: string | null;
}

function todayBkk(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
}

function actionError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function ReferenceSkeleton() {
  return (
    <div className="card p-5 space-y-4 animate-pulse" aria-label="กำลังโหลดข้อมูลแบบฟอร์ม">
      <div className="h-5 w-1/2 bg-gray-100 rounded" />
      <div className="h-11 w-full bg-gray-100 rounded" />
      <div className="h-11 w-full bg-gray-100 rounded" />
      <div className="h-28 w-full bg-gray-100 rounded" />
    </div>
  );
}

export function HRPolicyNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const [search] = useSearchParams();
  const fromId = search.get('from');
  const versionMode = Boolean(fromId);
  const policiesQuery = usePolicies();
  const departmentsQuery = useOrgDepartments();
  const employeesQuery = useEmployees();
  const create = useCreatePolicy();
  const createVersion = useCreatePolicyVersion();
  const initialized = useRef(false);

  const employees = useMemo<EmployeeOption[]>(() => {
    const raw = employeesQuery.data as { employees?: EmployeeOption[] } | EmployeeOption[] | undefined;
    const list = Array.isArray(raw) ? raw : (raw?.employees ?? []);
    return list.filter((employee) => employee.status === 'active' || employee.status === 'probation');
  }, [employeesQuery.data]);
  const departments = departmentsQuery.data ?? [];
  const branches = departments.filter((department) => !department.parent_department_id);
  const oldPolicy = fromId ? policiesQuery.data?.policies.find((policy) => policy.id === fromId) : undefined;

  const [kind, setKind] = useState<PolicyKind>('work_rule');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayBkk());
  const [effectiveTo, setEffectiveTo] = useState('');
  const [scope, setScope] = useState<PolicyAudience['scope']>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [ackRequired, setAckRequired] = useState(true);
  const [ackMode, setAckMode] = useState<PolicyAckMode>('button');
  const [file, setFile] = useState<File | null>(null);
  const [workingText, setWorkingText] = useState('');
  const [pendingUpload, setPendingUpload] = useState<{ url: string; file: File; docNo: string; version: number } | null>(null);

  useEffect(() => {
    if (!oldPolicy || initialized.current) return;
    initialized.current = true;
    setKind(oldPolicy.kind);
    setTitle(oldPolicy.title);
    setSummary(oldPolicy.summary ?? '');
    setEffectiveFrom(oldPolicy.effective_from);
    setEffectiveTo(oldPolicy.effective_to ?? '');
    setScope(oldPolicy.audience.scope);
    if (oldPolicy.audience.scope === 'dept') setSelectedIds(oldPolicy.audience.dept_ids);
    else if (oldPolicy.audience.scope === 'branch') setSelectedIds(oldPolicy.audience.branch_ids);
    else if (oldPolicy.audience.scope === 'emp') setSelectedIds(oldPolicy.audience.emp_codes);
    else setSelectedIds([]);
    setAckRequired(oldPolicy.ack_required);
    setAckMode(oldPolicy.ack_mode);
  }, [oldPolicy]);

  function toggleSelected(value: string) {
    setSelectedIds((current) => current.includes(value)
      ? current.filter((id) => id !== value)
      : [...current, value]);
  }

  function audienceValue(): PolicyAudience {
    if (scope === 'dept') return { scope, dept_ids: selectedIds };
    if (scope === 'branch') return { scope, branch_ids: selectedIds };
    if (scope === 'emp') return { scope, emp_codes: selectedIds };
    return { scope: 'all' };
  }

  function validate(): boolean {
    if (!title.trim()) { toast.error('กรุณากรอกชื่อเอกสาร'); return false; }
    if (!effectiveFrom) { toast.error('กรุณาระบุวันที่เริ่มใช้'); return false; }
    if (effectiveTo && effectiveTo < effectiveFrom) { toast.error('วันที่สิ้นสุดต้องไม่มาก่อนวันที่เริ่มใช้'); return false; }
    if (scope !== 'all' && selectedIds.length === 0) {
      toast.error(scope === 'emp' ? 'กรุณาเลือกพนักงานอย่างน้อย 1 คน' : 'กรุณาเลือกหน่วยงานอย่างน้อย 1 แห่ง');
      return false;
    }
    if (!file) { toast.error('กรุณาเลือกไฟล์ PDF'); return false; }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('รองรับเฉพาะไฟล์ PDF'); return false;
    }
    if (file.size > 25 * 1024 * 1024) { toast.error('ไฟล์ PDF ต้องไม่เกิน 25 MB'); return false; }
    return true;
  }

  async function retryUpload() {
    if (!pendingUpload) return;
    setWorkingText('กำลังอัปโหลด PDF…');
    try {
      await putPresignedFile(pendingUpload.url, pendingUpload.file, 'application/pdf');
      toast.success(`อัปโหลด ${pendingUpload.docNo} ฉบับที่ ${pendingUpload.version} เรียบร้อยแล้ว`);
      navigate('/hr/policies');
    } catch (e) {
      toast.error(actionError(e, 'อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setWorkingText('');
    }
  }

  async function submit() {
    if (pendingUpload) { await retryUpload(); return; }
    if (!validate() || !file) return;
    setWorkingText('กำลังตรวจไฟล์…');
    try {
      const fileInfo = {
        file_name: file.name,
        file_size: file.size,
        content_type: 'application/pdf' as const,
        file_sha256: await sha256Hex(file),
      };
      const base = {
        title: title.trim(),
        summary: summary.trim() || null,
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
        audience: audienceValue(),
        ack_required: ackRequired,
        ack_mode: ackRequired ? ackMode : 'button' as PolicyAckMode,
        ...fileInfo,
      };
      setWorkingText('กำลังสร้างร่าง…');
      const response = versionMode && fromId
        ? await createVersion.mutateAsync({ id: fromId, body: base })
        : await create.mutateAsync({ ...base, kind } as CreatePolicyInput);

      setPendingUpload({
        url: response.upload.url,
        file,
        docNo: response.policy.doc_no,
        version: response.policy.version,
      });
      setWorkingText('กำลังอัปโหลด PDF…');
      await putPresignedFile(response.upload.url, file, response.upload.content_type);
      toast.success(
        versionMode
          ? `สร้างร่าง ${response.policy.doc_no} ฉบับที่ ${response.policy.version} แล้ว ฉบับเดิมยังเผยแพร่จนกว่าจะเผยแพร่ฉบับนี้สำเร็จ`
          : `สร้างร่าง ${response.policy.doc_no} ฉบับที่ ${response.policy.version} และอัปโหลด PDF แล้ว`,
      );
      navigate('/hr/policies');
    } catch (e) {
      toast.error(actionError(e, 'สร้างร่างเอกสารไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setWorkingText('');
    }
  }

  const loadingReferences = departmentsQuery.isLoading || employeesQuery.isLoading || (versionMode && policiesQuery.isLoading);
  const referenceError = departmentsQuery.error || employeesQuery.error || (versionMode ? policiesQuery.error : null);
  const busy = Boolean(workingText) || create.isPending || createVersion.isPending;
  const choices: Array<{ value: string; label: string; meta?: string }> = scope === 'emp'
    ? employees.map((employee) => ({
        value: employee.emp_code,
        label: `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim() || employee.emp_code,
        meta: employee.emp_code,
      }))
    : (scope === 'branch' ? branches : departments).map((department) => ({
        value: department.id,
        label: department.name,
      }));

  return (
    <AdminLayout title={versionMode ? 'ออกเอกสารฉบับใหม่' : 'เพิ่มเอกสารข้อบังคับ'}>
      <div className="space-y-4 max-w-3xl">
        <button className="btn-secondary min-h-11" onClick={() => navigate('/hr/policies')} disabled={busy}>
          <ArrowLeft className="w-4 h-4" /> กลับไปรายการ
        </button>

        {loadingReferences ? (
          <ReferenceSkeleton />
        ) : referenceError ? (
          <LoadError
            error={referenceError}
            onRetry={() => { void departmentsQuery.refetch(); void employeesQuery.refetch(); void policiesQuery.refetch(); }}
          />
        ) : versionMode && !oldPolicy ? (
          <LoadError hint="ไม่พบฉบับเดิม อาจถูกเปลี่ยนสถานะไปแล้ว" onRetry={() => void policiesQuery.refetch()} />
        ) : (
          <>
            {versionMode && oldPolicy && (
              <div className="card p-4 border-l-4 border-l-amber-400 text-sm text-gray-700">
                กำลังออกฉบับใหม่แทนเลขที่ {oldPolicy.doc_no} ฉบับที่ {oldPolicy.version}
                <span className="block mt-1 text-gray-500">
                  ฉบับเดิมยังเผยแพร่ตามปกติ และจะถูกเก็บถาวรพร้อมกันเมื่อเผยแพร่ฉบับใหม่นี้สำเร็จ
                </span>
              </div>
            )}

            <div className="card p-5 space-y-4">
              <div>
                <label className="label">ประเภทเอกสาร</label>
                <select className="input w-full min-h-11 text-base" value={kind} onChange={(e) => setKind(e.target.value as PolicyKind)} disabled={versionMode || busy}>
                  <option value="work_rule">ข้อบังคับเกี่ยวกับการทำงาน</option>
                  <option value="regulation">ระเบียบ / คำสั่ง</option>
                  <option value="announcement">ประกาศ</option>
                </select>
              </div>
              <div>
                <label className="label">ชื่อเอกสาร</label>
                <input className="input w-full min-h-11 text-base" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} disabled={busy || Boolean(pendingUpload)} />
              </div>
              <div>
                <label className="label">สรุปสำหรับพนักงาน</label>
                <textarea className="input w-full text-base" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={2000} disabled={busy || Boolean(pendingUpload)} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">วันที่เริ่มใช้</label>
                  <input type="date" className="input w-full min-h-11 text-base" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} disabled={busy || Boolean(pendingUpload)} />
                </div>
                <div>
                  <label className="label">วันที่สิ้นสุด (ถ้ามี)</label>
                  <input type="date" className="input w-full min-h-11 text-base" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} disabled={busy || Boolean(pendingUpload)} />
                </div>
              </div>

              <div>
                <label className="label">ขอบเขตพนักงาน</label>
                <select
                  className="input w-full min-h-11 text-base"
                  value={scope}
                  onChange={(e) => { setScope(e.target.value as PolicyAudience['scope']); setSelectedIds([]); }}
                  disabled={busy || Boolean(pendingUpload)}
                >
                  <option value="all">ทุกคน</option>
                  <option value="branch">สาขา (หน่วยงานระดับบนสุด)</option>
                  <option value="dept">แผนก</option>
                  <option value="emp">เลือกพนักงานรายคน</option>
                </select>
                {scope !== 'all' && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {choices.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500">
                        {scope === 'emp' ? 'ยังไม่มีพนักงานที่ใช้งานอยู่' : 'ยังไม่มีหน่วยงานในโครงสร้างองค์กร'}
                      </p>
                    ) : choices.map((choice) => {
                      return (
                        <label key={choice.value} className="flex items-center gap-3 px-4 min-h-11 text-sm cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="w-5 h-5"
                            checked={selectedIds.includes(choice.value)}
                            onChange={() => toggleSelected(choice.value)}
                            disabled={busy || Boolean(pendingUpload)}
                          />
                          <span className="flex-1">{choice.label}</span>
                          {choice.meta && <span className="text-xs text-gray-500">{choice.meta}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-3 min-h-11 text-sm">
                <input type="checkbox" className="w-5 h-5" checked={ackRequired} onChange={(e) => setAckRequired(e.target.checked)} disabled={busy || Boolean(pendingUpload)} />
                บังคับให้พนักงานกดรับทราบ
              </label>
              {ackRequired && (
                <div>
                  <label className="label">วิธีรับทราบ</label>
                  <select className="input w-full min-h-11 text-base" value={ackMode} onChange={(e) => setAckMode(e.target.value as PolicyAckMode)} disabled={busy || Boolean(pendingUpload)}>
                    <option value="button">กดปุ่มรับทราบ</option>
                    <option value="signature">วาดลายเซ็นก่อนรับทราบ</option>
                  </select>
                </div>
              )}

              <div>
                <label className="label">ไฟล์ PDF</label>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  className="input w-full min-h-11 text-base file:mr-3 file:border-0 file:bg-transparent"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  disabled={busy || Boolean(pendingUpload)}
                />
                <p className="text-xs text-gray-500 mt-1">รองรับ PDF ขนาดไม่เกิน 25 MB ระบบจะตรวจขนาดและลายนิ้วมือไฟล์ก่อนเผยแพร่</p>
              </div>
            </div>

            {pendingUpload && (
              <div className="card p-4 border-l-4 border-l-amber-400 text-sm">
                สร้างร่างแล้ว แต่ไฟล์ยังอัปโหลดไม่สำเร็จ กดปุ่มด้านล่างเพื่อลองอัปโหลดไฟล์เดิมอีกครั้ง
              </div>
            )}
            <button className="btn-cta w-full min-h-12" onClick={() => void submit()} disabled={busy}>
              {pendingUpload ? <RotateCw className="w-5 h-5" /> : <FileUp className="w-5 h-5" />}
              {workingText || (pendingUpload ? 'ลองอัปโหลดอีกครั้ง' : versionMode ? 'สร้างร่างฉบับใหม่และอัปโหลด' : 'สร้างร่างและอัปโหลด')}
            </button>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
